import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { AuthImg } from "./AuthImg";
import { Wrench, ChevronDown, ChevronUp, Pencil, Trash2, Check, X } from "lucide-react";
import { fmtRoDate } from "../lib/dateFmt";
import { toast } from "sonner";

const TYPE_LABELS = {
  liquids: "🛢️ Control lichide",
  photos_actual: "📷 Fotografii actuale",
  tires: "🛞 Profil cauciucuri",
  cleaning: "✨ Curățenie",
  km_friday: "📊 Poza km",
};

const carName = (e) => [e.car_label, e.car_plate].filter(Boolean).join(" · ") || "—";
const carWithDriver = (e) => carName(e) + (e.driver_label ? ` · 🧑‍✈️ ${e.driver_label}` : "");

function EntryRow({ e, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(e.notes || "");
  const [km, setKm] = useState(e.data?.km ?? "");
  const [busy, setBusy] = useState(false);

  const saveEdit = async () => {
    setBusy(true);
    try {
      const payload = { notes };
      if (e.type === "km_friday" && km !== "") payload.km = parseFloat(km) || 0;
      await api.put(`/me/car-maintenance/${e.id}`, payload);
      toast.success("Intrare actualizată");
      setEditing(false);
      onChanged();
    } catch {
      toast.error("Eroare la salvare");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Ștergi înregistrarea „${TYPE_LABELS[e.type] || e.type}" de la ${carName(e)}?`)) return;
    try {
      await api.delete(`/me/car-maintenance/${e.id}`);
      toast.success("Intrare ștearsă");
      onChanged();
    } catch {
      toast.error("Eroare la ștergere");
    }
  };

  return (
    <div className="border border-zinc-200 rounded-md p-2.5 bg-zinc-50/60" data-testid={`admin-maint-entry-${e.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap text-xs">
        <div className="font-semibold">
          {TYPE_LABELS[e.type] || e.type}
          {e.type === "km_friday" && e.data?.km != null && !editing && (
            <span className="text-orange-700 font-bold ml-1.5">{Number(e.data.km).toLocaleString("ro-RO")} km</span>
          )}
        </div>
        <div className="text-zinc-500 flex items-center gap-2">
          <span>🚙 <strong>{carWithDriver(e)}</strong> · 👤 {e.user_label || "—"} · 📅 {fmtRoDate(e.ts)}</span>
          {!editing && (
            <>
              <button onClick={() => setEditing(true)} className="p-1 rounded hover:bg-teal-100 text-teal-700" title="Editează" data-testid={`admin-maint-edit-${e.id}`}>
                <Pencil size={13} />
              </button>
              <button onClick={del} className="p-1 rounded hover:bg-red-100 text-red-600" title="Șterge" data-testid={`admin-maint-delete-${e.id}`}>
                <Trash2 size={13} />
              </button>
            </>
          )}
        </div>
      </div>

      {editing ? (
        <div className="mt-2 space-y-1.5">
          {e.type === "km_friday" && (
            <input
              type="number"
              value={km}
              onChange={(ev) => setKm(ev.target.value)}
              className="w-full border border-zinc-300 rounded px-2 py-1 text-xs"
              placeholder="Kilometraj"
              data-testid={`admin-maint-km-input-${e.id}`}
            />
          )}
          <textarea
            value={notes}
            onChange={(ev) => setNotes(ev.target.value)}
            rows={2}
            className="w-full border border-zinc-300 rounded px-2 py-1 text-xs"
            placeholder="Observații..."
            data-testid={`admin-maint-notes-input-${e.id}`}
          />
          <div className="flex gap-2">
            <button onClick={saveEdit} disabled={busy} className="text-xs px-2 py-1 rounded bg-teal-600 text-white font-bold flex items-center gap-1" data-testid={`admin-maint-save-${e.id}`}>
              <Check size={12} /> Salvează
            </button>
            <button onClick={() => setEditing(false)} className="text-xs px-2 py-1 rounded border border-zinc-300 flex items-center gap-1">
              <X size={12} /> Renunță
            </button>
          </div>
        </div>
      ) : (
        <>
          {e.notes && <div className="text-[11px] italic text-zinc-600 mt-1">&bdquo;{e.notes}&rdquo;</div>}
          {(e.image_ids || []).length > 0 && (
            <div className="flex gap-1.5 mt-1.5 flex-wrap">
              {e.image_ids.slice(0, 8).map((fid, idx) => (
                <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer" className="w-12 h-12 rounded overflow-hidden border border-zinc-300 hover:border-teal-600 block">
                  <AuthImg fileId={fid} className="w-full h-full object-cover" alt={`Foto ${idx + 1}`} />
                </a>
              ))}
              {(e.image_ids || []).length > 8 && (
                <span className="w-12 h-12 rounded border border-zinc-300 grid place-items-center text-[10px] text-zinc-500">+{e.image_ids.length - 8}</span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MissingCarRow({ m }) {
  const navigate = useNavigate();
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const sendTask = async () => {
    setBusy(true);
    try {
      await api.post("/tasks", {
        title: `🔧 Întreținere mașină — ${m.plate || m.car}`,
        description: `Completează urgent în rubrica ÎNTREȚINERE MAȘINI pentru ${[m.car, m.plate].filter(Boolean).join(" · ")}: ${(m.missing || []).join(", ")} (săptămâna curentă).`,
        assigned_person_ids: [m.driver_person_id],
        assigned_user_ids: [],
      });
      toast.success(`Task trimis către ${m.driver}`);
      setSent(true);
    } catch {
      toast.error("Eroare la trimiterea task-ului");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-2 text-xs flex-wrap" data-testid={`maint-sunday-car-${m.car_id}`}>
      <span className="font-semibold">
        <button
          onClick={() => navigate(`/autoturisme/${m.car_id}`)}
          className="hover:underline hover:text-red-900 font-semibold"
          title="Deschide pagina mașinii"
          data-testid={`maint-missing-car-link-${m.car_id}`}
        >
          🚙 {[m.car, m.plate].filter(Boolean).join(" · ")}
        </button>
        {m.driver && <span className="text-red-900"> · 🧑‍✈️ {m.driver}</span>}
      </span>
      <span className="flex items-center gap-2">
        <span className="text-red-700 font-semibold">Lipsă: {(m.missing || []).join(" + ")}</span>
        {m.driver_person_id && (
          <button
            onClick={sendTask}
            disabled={busy || sent}
            className={`px-2 py-0.5 rounded font-bold text-[11px] ${sent ? "bg-emerald-100 text-emerald-700" : "bg-red-600 hover:bg-red-700 text-white"}`}
            title={`Trimite task șoferului ${m.driver}`}
            data-testid={`maint-send-task-${m.car_id}`}
          >
            {sent ? "✓ Task trimis" : busy ? "Se trimite..." : "📩 Trimite task"}
          </button>
        )}
      </span>
    </div>
  );
}

// Card pe Dashboard admin: colapsat implicit — click pentru a vedea/edita/șterge ce urcă șoferii.
export default function AdminMaintenanceCard() {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState(null);
  const [sundayReport, setSundayReport] = useState(null);

  useEffect(() => {
    api.get("/car-maintenance/cars-missing-report").then((r) => setSundayReport(r.data)).catch(() => {});
  }, []);

  const load = () => {
    api.get("/me/car-maintenance", { params: { limit: 200 } }).then((r) => setEntries(r.data || [])).catch(() => setEntries([]));
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && entries === null) load();
  };

  return (
    <div className="card-flat p-6 border-l-4 border-l-[#0D9488] w-full mb-4" data-testid="admin-maintenance-card">
      <button onClick={toggle} className="w-full text-left flex items-start justify-between gap-3 group" data-testid="admin-maintenance-toggle">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-md bg-[#CCFBF1] text-[#0D9488] grid place-items-center shrink-0">
            <Wrench size={22} />
          </div>
          <div>
            <div className="small-label text-[#0D9488]">Rapoarte șoferi</div>
            <div className="font-display text-xl font-bold mt-1">ÎNTREȚINERE MAȘINI</div>
            <div className="text-xs text-zinc-500 mt-1">Click pentru a vedea fișierele urcate de șoferi — editare & ștergere</div>
          </div>
        </div>
        <span className="text-[#0D9488] mt-2">{open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}</span>
      </button>

      {/* Mașinile alocate săptămâna asta fără verificări complete (vizibil permanent) */}
      {sundayReport && (sundayReport.missing || []).length > 0 && (
        <div className="mt-4 border border-red-300 bg-red-50 rounded-md p-3" data-testid="maint-sunday-report">
          <div className="text-xs font-bold text-red-800 mb-2">
            🔴 Mașini fără verificări complete săptămâna aceasta ({(sundayReport.missing || []).length}):
          </div>
          <div className="space-y-1">
            {(sundayReport.missing || []).map((m) => (
              <MissingCarRow key={m.car_id} m={m} />
            ))}
          </div>
        </div>
      )}
      {sundayReport && (sundayReport.missing || []).length === 0 && (
        <div className="mt-4 border border-emerald-300 bg-emerald-50 rounded-md p-3 text-xs font-semibold text-emerald-800" data-testid="maint-sunday-ok">
          ✅ Toate mașinile folosite săptămâna asta au ulei, antigel și poza km încărcate.
        </div>
      )}

      {open && (
        <div className="mt-4 space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="admin-maintenance-list">
          {entries === null && <div className="text-xs text-zinc-400">Se încarcă...</div>}
          {entries !== null && entries.length === 0 && (
            <div className="text-xs text-zinc-400 italic">Nicio înregistrare încă — șoferii nu au urcat nimic.</div>
          )}
          {(entries || []).map((e) => (
            <EntryRow key={e.id} e={e} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}
